import { Button } from "@tracify/ui/button";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";

import { LinkSlashIcon } from "@heroicons/react/24/outline";
import {
  type PatchAdAccountCsidsInput,
  usePatchAdAccountCsids,
} from "@lib/api-hooks/useAdAccounts/usePatchAdAccountsCsids";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { Spinner } from "@tracify/ui/spinner";
import { useEffect, useMemo, useState } from "react";
import type {
  AdAccountIssues,
  ConnectedAdAccounts,
} from "@lib/api-hooks/useAdAccounts/types";
import { useConnectedAdAccounts } from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import { useAdAccountIssues } from "@lib/api-hooks/useAdAccounts/useAdAccountIssues";
import { toast } from "sonner";
import ManageOutbrainAccountsModal from "./ManageOutbrainAccountModal";
import ManageTaboolaAccountsModal from "./ManageTaboolaAccountsModal";
import { Banner } from "@tracify/ui/banner";
import { usePathname, useSearchParams } from "next/navigation";
import { SafetyDialog } from "@tracify/ui/safety-dialog";
import { useUnsavedChanges } from "@lib/hooks/use-unsaved-changes";
import { useUnlinkAdAccount } from "@lib/api-hooks/useAdAccounts/useUnlinkAdAccount";
import {
  useCheckConnections,
  type CheckConnectionsResponse,
} from "@lib/api-hooks/useAdAccounts/useCheckConnections";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tracify/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@tracify/ui/dropdown-menu";
import {
  ShieldCheckIcon,
  EllipsisVerticalIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useTaboolaConnection } from "@components/Dashboards/SettingsDashboard/TaboolaConnectionContext";
import { MultiSelect } from "@tracify/ui/multi-select";
import InitializeAdAccountFlowModal from "@components/Dashboards/SettingsDashboard/InitializeAdAccountFlowModal";
import AssignAdAccountModal from "@components/AdAccounts/AssignAdAccountModal";
import Image from "next/image";

import { useAuth } from "@lib/api-hooks";
import { useRequestCreateSiteref } from "@lib/api-hooks/useSiteRefs";
import { DEPLOY_ENVIRONMENT } from "@/constants/constants";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import { integrations } from "@components/Dashboards/SettingsDashboard/CombinedIntegrationsDashboard";
import { usePostHog } from "posthog-js/react";
import { captureAdAccountConnectionUpdated } from "@/lib/ad-account-connection-analytics";
import { Badge } from "@tracify/ui/badge";

type Props = {
  type:
    | "google"
    | "facebook"
    | "tiktok"
    | "pinterest"
    | "outbrain"
    | "msads"
    | "taboola";
};

const AssignAdAccountsCard = ({ type }: Props) => {
  const posthog = usePostHog();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<{
    accountId: string;
    name: string;
    source: string;
  } | null>(null);
  const [connectionResults, setConnectionResults] =
    useState<CheckConnectionsResponse | null>(null);
  const {
    stores,
    selectedOrganisationData,
    isPending: isPendingSelectedStores,
    selectedStoreIds,
  } = useSelectedStores();
  const { shouldUseIncludeAll, setShouldUseIncludeAll } =
    useTaboolaConnection();
  const { data: connectedAdAccounts, isFetching } = useConnectedAdAccounts({
    organisationId: selectedOrganisationData?.id ?? "",
    includeAll: type === "taboola" && shouldUseIncludeAll,
  });
  const { data: adAccountIssues } = useAdAccountIssues({
    enabled: Boolean(selectedOrganisationData?.id),
  });

  const { data: auth } = useAuth();
  const requestSiterefCreateMutation = useRequestCreateSiteref();
  const [selectedStoreModalOpen, setSelectStoreModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const integrationType = type === "facebook" ? "fb" : type;
  const integrationConfig = integrations[integrationType];

  const onModalOpen = () => {
    setSelectStoreModalOpen(true);
  };

  const onModalClose = (success?: boolean) => {
    setSelectStoreModalOpen(false);
    if (integrationType === "outbrain" && success) {
      setAssignModalOpen(true);
      return;
    }
    if (integrationType === "taboola" && success) {
      setShouldUseIncludeAll(true);
      setAssignModalOpen(true);
    }
  };

  const pathname = usePathname() ?? "/";
  const searchParams = useSearchParams();
  const currentSearch = searchParams?.toString() ?? "";
  const asPath = `${pathname}${currentSearch ? `?${currentSearch}` : ""}`;
  const mappingSuccess = searchParams?.get("mapping_success");

  const connectButtonDisabled =
    (integrationConfig?.disabled ?? false) ||
    !["production", "development"].includes(DEPLOY_ENVIRONMENT);

  const tooltipMessage =
    selectedStoreIds.length > 1 ? (
      "Please select exactly 1 store from the store selector on the top-left to connect an ad account."
    ) : !!auth?.payload?.rol && auth?.payload?.rol >= 128 ? (
      "Admin users can't connect ad accounts."
    ) : (
      <div className="max-w-xs sm:max-w-sm md:w-96">
        Ad integrations are only enabled in our production dashboard. Please go
        to{" "}
        <a
          href={`https://dashboard.tracify.ai${asPath ?? ""}`}
          className="underline"
          target="_blank"
        >
          https://dashboard.tracify.ai
        </a>{" "}
        to access them.
      </div>
    );

  const allAdAccounts = useMemo(() => {
    return Object.values(connectedAdAccounts?.accounts ?? {}).flat();
  }, [connectedAdAccounts]);

  const customerSites = selectedOrganisationData?.customerSites.map(
    (el) => el.id
  );

  const adAccountsForChannel = useMemo(() => {
    return (
      connectedAdAccounts?.accounts?.[
        type as keyof ConnectedAdAccounts["accounts"]
      ] ?? []
    );
  }, [connectedAdAccounts, type]);

  const issuesForChannel =
    adAccountIssues?.[type as keyof AdAccountIssues] ?? {};
  const hasConnectionProblem = (accountId: string) =>
    Boolean(
      issuesForChannel[accountId]?.length ||
      connectionResults?.some(
        (result) => result.account_id === accountId && !result.is_valid
      )
    );

  const [accountToStoresMapping, setAccountToStoresMapping] = useState<{
    [key: string]: string[];
  }>({});

  const [originalMapping, setOriginalMapping] = useState<{
    [key: string]: string[];
  }>({});

  const updateAdAccountsMutation = usePatchAdAccountCsids();
  const unlinkAdAccountMutation = useUnlinkAdAccount();
  const checkConnectionsMutation = useCheckConnections();
  const organisationStores = stores?.filter((el) =>
    customerSites?.includes(el.csid)
  );

  const hasUnsavedChanges = useMemo(() => {
    return (
      JSON.stringify(accountToStoresMapping) !== JSON.stringify(originalMapping)
    );
  }, [accountToStoresMapping, originalMapping]);

  const { showDialog, handleLeave, handleStay } = useUnsavedChanges({
    hasUnsavedChanges,
    onLeave: () => {
      setAccountToStoresMapping(originalMapping);
    },
  });

  useEffect(() => {
    // Skip if still loading
    if (
      isFetching ||
      isPendingSelectedStores ||
      !selectedOrganisationData?.id
    ) {
      return;
    }

    // Build the mapping from the fetched data
    const mapping =
      allAdAccounts?.reduce(
        (acc, account) => {
          acc[account.account_id] = account.connected_stores.map(
            (store) => store.csid
          );
          return acc;
        },
        {} as Record<string, string[]>
      ) ?? {};

    setAccountToStoresMapping(mapping);
    setOriginalMapping(mapping);
    setConnectionResults(null);
  }, [
    selectedOrganisationData?.id,
    isFetching,
    isPendingSelectedStores,
    allAdAccounts,
  ]);

  const successfullyMapped = useMemo(() => {
    return (
      mappingSuccess === type &&
      adAccountsForChannel.some(
        (el) => accountToStoresMapping[el.account_id]?.length
      )
    );
  }, [mappingSuccess, type, adAccountsForChannel, accountToStoresMapping]);

  const handleConnect = async () => {
    try {
      const mappings: PatchAdAccountCsidsInput["mappings"] = [];
      const updatedAccounts: Array<{ platform: string; accountId: string }> =
        [];
      for (const [adAccountId, csids] of Object.entries(
        accountToStoresMapping
      )) {
        const adAccount = allAdAccounts?.find(
          (el) => el.account_id === adAccountId
        );
        if (!adAccount) continue;
        mappings.push({
          source: adAccount.source,
          account_id: adAccount.account_id,
          associated_csids: csids,
        });

        const originalCsids = originalMapping[adAccountId] ?? [];
        const mappingChanged =
          originalCsids.length !== csids.length ||
          originalCsids.some((csid) => !csids.includes(csid));
        if (mappingChanged) {
          updatedAccounts.push({
            platform: adAccount.source,
            accountId: adAccount.account_id,
          });
        }
      }
      await updateAdAccountsMutation.mutateAsync({ mappings });
      updatedAccounts.forEach(({ platform, accountId }) => {
        captureAdAccountConnectionUpdated(
          posthog,
          platform,
          accountId,
          "store_mapping"
        );
      });
      setOriginalMapping({ ...accountToStoresMapping });
    } catch (error) {
      toast.error(
        (error as any)?.message ??
          "An error occurred while connecting accounts."
      );
    }
  };
  const handleReset = () => {
    setAccountToStoresMapping({ ...originalMapping });
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      await unlinkAdAccountMutation.mutateAsync({
        source: accountToDelete.source as any,
        accountId: accountToDelete.accountId,
      });
      toast.success(`Successfully unlinked ${accountToDelete.name}`);
      setDeleteConfirmOpen(false);
      setAccountToDelete(null);
      setConnectionResults(null);
    } catch (error) {
      toast.error(
        (error as Error)?.message ??
          "An error occurred while unlinking the account."
      );
    }
  };

  const handleTestConnections = async () => {
    if (!selectedOrganisationData?.id) return;

    try {
      const result = await checkConnectionsMutation.mutateAsync({
        organisation_id: selectedOrganisationData.id,
        source: type,
      });
      setConnectionResults(result);

      const invalidCount = result.filter((r) => !r.is_valid).length;
      if (invalidCount > 0) {
        toast.error(`${invalidCount} account(s) have invalid connections`);
      } else {
        toast.success("All accounts are connected successfully");
      }
    } catch (error) {
      toast.error(
        (error as Error)?.message ??
          "An error occurred while testing connections."
      );
    }
  };

  // Only return null when we're certain there are no accounts:
  // - Organisation must be loaded (to ensure query is enabled)
  // - Query must not be fetching
  // - No accounts found for this channel
  const noAccounts =
    !!selectedOrganisationData?.id &&
    adAccountsForChannel?.length === 0 &&
    !isFetching;

  const buttonComponent = connectButtonDisabled ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="primary"
          disabled={connectButtonDisabled}
          loading={requestSiterefCreateMutation.isPending}
          onClick={onModalOpen}
          className="w-full sm:w-auto ml-2 bg-[#5d5fef] hover:bg-[#5d5fef]/90 text-white"
        >
          <span className="flex items-center justify-center">
            <PlusIcon className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Connect new account</span>
            <span className="sm:hidden">Connect</span>
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltipMessage}</TooltipContent>
    </Tooltip>
  ) : (
    <Button
      variant="primary"
      disabled={connectButtonDisabled}
      loading={requestSiterefCreateMutation.isPending}
      onClick={onModalOpen}
      className="w-full sm:w-auto ml-2 bg-[#5d5fef] hover:bg-[#5d5fef]/90 text-white"
    >
      <span className="flex items-center justify-center">
        <PlusIcon className="h-5 w-5 sm:mr-2" />
        <span className="hidden sm:inline">Connect new account</span>
        <span className="sm:hidden">Connect</span>
      </span>
    </Button>
  );

  return (
    <>
      <SafetyDialog
        open={showDialog}
        onOpenChange={handleStay}
        onLeave={handleLeave}
        onStay={handleStay}
      />

      <InitializeAdAccountFlowModal
        type={integrationType}
        onClose={onModalClose}
        open={selectedStoreModalOpen}
        integration={integrations[integrationType]}
      />

      {(integrationType === "outbrain" || integrationType === "taboola") && (
        <AssignAdAccountModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          type={integrationType}
          includeAll={integrationType === "taboola" ? true : undefined}
        />
      )}

      <Card>
        {successfullyMapped ? (
          <Banner
            messages={[
              {
                id: "ad-accounts-success",
                type: "success",
                message: (
                  <p>
                    <span className="font-bold">All set!</span> Your ad accounts
                    have been successfully mapped to your stores.
                  </p>
                ),
                dismissible: false,
              },
            ]}
            className="rounded-b-none"
          />
        ) : null}
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              {integrationConfig?.img ? (
                <div className="bg-muted w-12 h-12 flex items-center justify-center rounded-lg shrink-0">
                  <Image
                    src={integrationConfig.img}
                    alt={`${integrationConfig.name} logo`}
                    width={28}
                    height={28}
                    className="h-7 w-7 object-contain"
                  />
                </div>
              ) : null}
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                <h1 className="text-lg sm:text-xl font-bold">Ad accounts</h1>
                {adAccountsForChannel?.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    <span className="text-sm">
                      {adAccountsForChannel.length} connected ad account
                      {adAccountsForChannel.length === 1 ? "" : "s"}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                variant="ghost"
                onClick={handleTestConnections}
                loading={checkConnectionsMutation.isPending}
                disabled={
                  checkConnectionsMutation.isPending ||
                  adAccountsForChannel.length === 0
                }
                className="w-full sm:w-auto"
              >
                {checkConnectionsMutation.isPending ? (
                  <Spinner className="mr-2 h-4 w-4" />
                ) : (
                  <ShieldCheckIcon className="h-4 w-4 mr-2" />
                )}
                Test Accounts
              </Button>
              {type === "outbrain" &&
              Boolean(connectedAdAccounts?.outbrain_credentials?.length) ? (
                <ManageOutbrainAccountsModal
                  credentials={connectedAdAccounts?.outbrain_credentials ?? []}
                  isPending={isFetching}
                />
              ) : null}
              {type === "taboola" &&
              Boolean(connectedAdAccounts?.taboola_credentials?.length) ? (
                <ManageTaboolaAccountsModal
                  credentials={connectedAdAccounts?.taboola_credentials ?? []}
                  isPending={isFetching}
                />
              ) : null}
              {buttonComponent}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isFetching ? (
            <div className="w-full flex flex-col items-center justify-center gap-4 p-4 bg-muted rounded-lg h-48 animate-pulse"></div>
          ) : (
            <>
              {noAccounts ? (
                <p className="text-sm text-muted-foreground">
                  No ad accounts connected yet.
                </p>
              ) : (
                <div className="mt-4">
                  <p className="text-sm">
                    Now that your account has been successfully connected to
                    your organization, you are able to connect each one of ad
                    accounts to their specific stores.
                  </p>
                  <p className="text-sm">
                    You don&apos;t have to connect every ad account, just the
                    ones out of which Tracify should pull data.
                  </p>
                  <div className="mt-6 space-y-4">
                    {adAccountsForChannel?.map((el) => (
                      <div
                        key={el.account_id}
                        className="rounded-lg p-4 !w-full !bg-background"
                      >
                        <div className="flex items-center justify-between mb-4 font-space-grotesk">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-bold">{el.name}</p>
                            <p className="text-sm">
                              ({el.secondary_account_id || el.account_id})
                            </p>
                            {hasConnectionProblem(el.account_id) ? (
                              <Badge variant="rose" size="xxs">
                                Has a problem
                              </Badge>
                            ) : null}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-8 w-8 p-0 text-white/70 hover:text-white"
                              >
                                <EllipsisVerticalIcon className="h-5 w-5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-red-500 focus:text-red-500 focus:bg-red-500/10 cursor-pointer"
                                onClick={() => {
                                  setAccountToDelete({
                                    accountId: el.account_id,
                                    name: el.name,
                                    source: el.source,
                                  });
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <LinkSlashIcon className="h-4 w-4 mr-2" />
                                Unlink Account
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="max-w-3xl space-y-4">
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-sm whitespace-nowrap">
                              Connected to:
                            </p>
                            <div className="flex-1 max-w-3xl">
                              <MultiSelect
                                options={[
                                  ...(organisationStores?.map((store) => ({
                                    label: store.description,
                                    value: store.csid,
                                  })) ?? []),
                                ]}
                                onValueChange={(value) => {
                                  setAccountToStoresMapping((curr) => {
                                    return {
                                      ...curr,
                                      [el.account_id]: value,
                                    };
                                  });
                                }}
                                value={
                                  accountToStoresMapping[el.account_id] ?? []
                                }
                                placeholder="This ad account is not assigned to a store"
                                maxCount={3}
                                className="py-0"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row w-full justify-end gap-2 mt-4">
                    <Button
                      variant="ghost"
                      disabled={
                        updateAdAccountsMutation.isPending || !hasUnsavedChanges
                      }
                      onClick={handleReset}
                      className="w-full sm:w-auto"
                    >
                      Discard
                    </Button>
                    <Button
                      loading={updateAdAccountsMutation.isPending}
                      onClick={handleConnect}
                      disabled={
                        updateAdAccountsMutation.isPending || !hasUnsavedChanges
                      }
                      className="w-full sm:w-auto"
                    >
                      {updateAdAccountsMutation.isPending ? (
                        <Spinner className="mr-2 h-4 w-4" />
                      ) : null}
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-xl w-[calc(100vw-2rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle>Unlink Ad Account</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to unlink &quot;{accountToDelete?.name}&quot;?
            This will remove all token and credential information for this
            account. You can reconnect it later if needed.
          </p>
          <DialogFooter className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setAccountToDelete(null);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              loading={unlinkAdAccountMutation.isPending}
              className="w-full sm:w-auto"
            >
              {unlinkAdAccountMutation.isPending ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : null}
              Unlink Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssignAdAccountsCard;
